import { type DriverWeekTrip, type MeResponse, TRANSPORT_DRIVER_WEEK_LIMIT, type VehicleType } from "@seagames/contracts";
import { type StatsCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { errorMessage, isTransientError } from "../../lib/api/messages";
import { driverWeekOptions } from "../../lib/api/transport-fleet";
import { formatClockIn, formatDateTime, formatDayShort, zoneAbbreviation } from "../../lib/format/datetime";
import { canDispatch, canWriteFleet } from "../../lib/transport/access";
import { todayIn } from "../../lib/travel/view";
import { EmptyCard } from "../common/empty-card";
import { usePageBar } from "../shell/page-bar";
import { ChevronLeftIcon, ChevronRightIcon, PencilIcon } from "../transport/icons";
import { TripStatusChip } from "../transport/status";
import { Avatar } from "../users/ui";
import { DriverFormModal } from "./driver-form-modal";
import { useCallingCodes } from "./hooks";
import { addDaysYmd, isoDow } from "./model";
import { DriverNowChip, InactiveChip, PhoneLink, ShiftText, VehicleTypeIcon, useVehicleTypeLabel, useWeekdayName } from "./parts";

/**
 * A driver's week (her `pgDrv`, `#/tpdrv/<id>`): who they are, then seven
 * day cards, Monday first, with the trips their allocations hold that week
 * (`GET /transport/drivers/:id/week`, one gist range read). Hers had four
 * hard-coded weeks; ours steps a week at a time, any week. The rest day is
 * marked on its card. Times are the games' clock ("MYT") with the viewer's
 * own in the tooltip (PLAN X18).
 *
 * S4's route (`logistics.transportation.drivers.$driverId.tsx`) spreads
 * `driverWeekSearchShape` and passes `week` / `onWeek`.
 */
export const driverWeekSearchShape = {
	week: z
		.string()
		.regex(/^\d{4}-\d{2}-\d{2}$/)
		.optional()
		.catch(undefined),
};

export interface DriverWeekViewProps {
	driverId: string;
	session: Pick<MeResponse, "active_role" | "group">;
	/** The games' IANA zone (the API answers in it too: `week.timezone`). */
	timeZone: string;
	/** The Monday shown, `YYYY-MM-DD`; undefined = this week. */
	week: string | undefined;
	onWeek: (monday: string | undefined) => void;
}

export function DriverWeekView({ driverId, session, timeZone, week, onWeek }: DriverWeekViewProps) {
	const { t } = useTranslation("transportFleet");
	const { t: tc } = useTranslation("common");
	const { formatNumber } = useUiStrings();
	const weekday = useWeekdayName();
	const callingCodes = useCallingCodes();
	const data = useQuery(driverWeekOptions(driverId, week));
	const [editing, setEditing] = useState(false);
	const d = data.data?.driver;
	const zone = zoneAbbreviation(timeZone);
	const canWrite = canWriteFleet(session);
	// The trip page is the desk's; a provider reads the trip here, inline.
	const desk = canDispatch(session);

	usePageBar({
		title: d?.name ?? t("week.title"),
		subtitle: t("week.subtitle"),
		back: { to: "/logistics/transportation/fleet", label: t("week.back") },
	});

	if (data.isPending) {
		return (
			<div className="grid gap-[1rem]" aria-busy>
				<div className="skeleton h-[4.5rem] rounded-[0.625rem]" />
				<div className="skeleton h-[20rem] rounded-[0.625rem]" />
			</div>
		);
	}
	if (data.isError || !data.data || !d) {
		return (
			<EmptyCard
				tone="bad"
				title={t("week.loadFailed")}
				actions={
					<>
						{isTransientError(data.error) && (
							<button type="button" className="btn btn-sm btn-primary" onClick={() => void data.refetch()}>
								{tc("action.retry")}
							</button>
						)}
						<Link to="/logistics/transportation/fleet" className="btn btn-sm btn-line">
							{t("week.back")}
						</Link>
					</>
				}
			>
				{errorMessage(data.error)}
			</EmptyCard>
		);
	}

	const w = data.data;
	const today = todayIn(w.timezone || timeZone);
	const days = Array.from({ length: 7 }, (_, i) => addDaysYmd(w.start, i));
	const byDay = new Map<string, DriverWeekTrip[]>();
	for (const trip of w.trips) byDay.set(trip.local_date, [...(byDay.get(trip.local_date) ?? []), trip]);
	const isThisWeek = days.includes(today);
	// The week row names the plate; its type comes from the driver's usual vehicles when it is one of them.
	const typeOfPlate = new Map(d.usual_vehicles.map((v) => [v.plate, v.vehicle_type]));

	const cells: StatsCell[] = [
		{ key: "cluster", label: t("week.strip.cluster"), value: <span className="font-sans text-[0.9375rem] font-semibold">{d.cluster.name}</span> },
		{ key: "types", label: t("week.strip.certified"), value: <CertifiedText types={d.vehicle_types} /> },
		{ key: "shift", label: t("week.strip.shift"), value: <span className="font-sans text-[0.9375rem] font-semibold"><ShiftText start={d.shift_start} end={d.shift_end} /></span> },
		{ key: "rest", label: t("week.strip.restDay"), value: <span className="font-sans text-[0.9375rem] font-semibold">{d.rest_isodow ? weekday(d.rest_isodow) : t("shift.noRestShort")}</span> },
		{ key: "trips", label: t("week.strip.trips"), value: w.trips.length },
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1rem]">
			{/* Her `.l3hd`: initials, the phone, the usual plates; the status now and Edit on the right. */}
			<header className="flex flex-wrap items-center gap-[0.75rem] rounded-box border border-gms-line bg-base-100 px-[1rem] py-[0.75rem]">
				<Avatar name={d.name} />
				<span className="grid min-w-0 flex-1">
					<PhoneLink phone={d.phone} callingCodes={callingCodes} className="text-[0.9375rem] font-semibold text-gms-navy-ink" />
					<span className="truncate text-[0.78125rem] text-gms-ink-3">
						{d.usual_vehicles.length ? d.usual_vehicles.map((v) => v.plate).join(", ") : t("week.noUsualVehicles")}
						{d.provider ? ` · ${d.provider.name}` : ""}
					</span>
				</span>
				{d.is_active ? <DriverNowChip status={d.status_now} /> : <InactiveChip />}
				{canWrite && (
					<button type="button" className="btn btn-line btn-sm gap-[0.375rem]" onClick={() => setEditing(true)}>
						<PencilIcon className="size-[0.9375rem]" />
						{t("week.edit")}
					</button>
				)}
			</header>

			<StatsStrip label={t("week.strip.label")} cells={cells} />

			<div className="flex flex-wrap items-end justify-between gap-[0.75rem]">
				<div>
					<h2 className="text-[0.96875rem] font-semibold text-gms-navy-ink">{t("week.assigned")}</h2>
					<p className="mt-[0.125rem] text-[0.78125rem] text-gms-ink-3">
						{t("week.range", { from: formatDayShort(w.start), to: formatDayShort(w.end), zone })}
					</p>
				</div>
				<div className="flex items-center gap-[0.375rem]">
					{!isThisWeek && (
						<button type="button" className="btn btn-line btn-sm" onClick={() => onWeek(undefined)}>
							{t("week.thisWeek")}
						</button>
					)}
					<button type="button" className={STEP} aria-label={t("week.previous")} title={t("week.previous")} onClick={() => onWeek(addDaysYmd(w.start, -7))}>
						<ChevronLeftIcon />
					</button>
					<button type="button" className={STEP} aria-label={t("week.next")} title={t("week.next")} onClick={() => onWeek(addDaysYmd(w.start, 7))}>
						<ChevronRightIcon />
					</button>
				</div>
			</div>

			<div className={`grid gap-[0.875rem] md:grid-cols-2 ${data.isFetching ? "opacity-60 transition-opacity" : "transition-opacity"}`}>
				{days.map((day) => {
					const trips = byDay.get(day) ?? [];
					const rest = d.rest_isodow !== null && isoDow(day) === d.rest_isodow;
					return (
						<section key={day} aria-labelledby={`dw-${day}`} className="grid content-start gap-[0.375rem]">
							<div className="flex items-baseline justify-between gap-[0.5rem] px-[0.125rem]">
								<h3 id={`dw-${day}`} className="text-[0.875rem] font-semibold text-gms-navy-ink">
									{formatDayShort(day)}
									{day === today && <span className="font-medium text-gms-ink-3"> · {t("week.today")}</span>}
								</h3>
								<span className="text-[0.78125rem] text-gms-ink-3">{rest && trips.length === 0 ? t("week.restDay") : t("week.trips", { count: trips.length, n: formatNumber(trips.length) })}</span>
							</div>
							<div className="overflow-hidden rounded-box border border-gms-line bg-base-100">
								{trips.length === 0 ? (
									<p className="flex items-center gap-[0.5rem] px-[0.875rem] py-[0.75rem] text-[0.8125rem] text-gms-ink-3">
										{rest && <SunGlyph />}
										{rest ? t("week.restNotAssignable") : t("week.noTrips")}
									</p>
								) : (
									<ul className="divide-y divide-gms-line">
										{trips.map((trip) => (
											<li key={trip.transport_allocation_id}>
												<TripLine trip={trip} timeZone={w.timezone || timeZone} vehicleType={typeOfPlate.get(trip.plate)} linked={desk} />
											</li>
										))}
									</ul>
								)}
							</div>
						</section>
					);
				})}
			</div>
			{data.data.trips.length >= TRANSPORT_DRIVER_WEEK_LIMIT && <p className="text-[0.75rem] text-gms-warn">{t("week.truncated")}</p>}

			{editing && <DriverFormModal target={{ mode: "edit", driver: d }} onClose={() => setEditing(false)} />}
		</div>
	);
}

const STEP =
	"grid size-[2.25rem] cursor-pointer place-items-center rounded-[0.5rem] border border-gms-line-2 bg-base-100 text-gms-navy-ink transition-colors hover:border-gms-navy [&>svg]:size-[1rem]";

/** One trip on a day card (her `.l3trip`): time · from → to · ref, plate, pax, contingent · status. */
function TripLine({ trip, timeZone, vehicleType, linked }: { trip: DriverWeekTrip; timeZone: string; vehicleType: VehicleType | undefined; linked: boolean }) {
	const { t } = useTranslation("transportFleet");
	const { formatNumber } = useUiStrings();
	const typeLabel = useVehicleTypeLabel();
	const layout = "grid grid-cols-[4.25rem_minmax(0,1fr)] gap-x-[0.75rem] gap-y-[0.25rem] px-[0.875rem] py-[0.625rem] sm:grid-cols-[4.25rem_minmax(0,1fr)_auto]";
	const body = (
		<>
			<span className="font-display text-[1.0625rem] leading-[1.2] font-bold text-gms-navy-ink tabular-nums" title={formatDateTime(trip.pickup_at)}>
				{formatClockIn(trip.pickup_at, timeZone)}
			</span>
			<span className="grid min-w-0 gap-[0.125rem]">
				<b className="text-[0.84375rem] leading-snug font-semibold text-gms-navy-ink">{t("week.route", { from: trip.from.name, to: trip.to.name })}</b>
				<span className="flex flex-wrap items-center gap-x-[0.375rem] text-[0.75rem] text-gms-ink-3">
					<span>{trip.ref}</span>
					<span aria-hidden>·</span>
					<span className="inline-flex items-center gap-[0.25rem]" title={vehicleType ? typeLabel(vehicleType) : undefined}>
						{vehicleType && <VehicleTypeIcon type={vehicleType} className="size-[0.8125rem]" />}
						<b className="font-semibold tracking-[0.02em] text-gms-ink-2">{trip.plate}</b>
					</span>
					<span aria-hidden>·</span>
					<span>{t("week.pax", { count: trip.pax, n: formatNumber(trip.pax) })}</span>
					{trip.organization_code && (
						<>
							<span aria-hidden>·</span>
							<span className="font-semibold text-gms-ink-2">{trip.organization_code}</span>
						</>
					)}
				</span>
			</span>
			<span className="col-start-2 sm:col-start-3 sm:row-start-1 sm:self-start">
				<TripStatusChip status={trip.status} />
			</span>
		</>
	);
	return linked ? (
		<Link
			to="/logistics/transportation/requests/$requestId"
			params={{ requestId: trip.transport_request_id }}
			className={`${layout} transition-colors hover:bg-gms-soft focus-visible:bg-gms-soft focus-visible:outline-none`}
		>
			{body}
		</Link>
	) : (
		<div className={layout}>{body}</div>
	);
}

function CertifiedText({ types }: { types: readonly VehicleType[] }) {
	const label = useVehicleTypeLabel();
	return <span className="font-sans text-[0.9375rem] font-semibold">{types.map(label).join(", ")}</span>;
}

function SunGlyph() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden className="size-[0.9375rem] shrink-0">
			<circle cx="12" cy="12" r="3.8" />
			<path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6 7 7M17 17l1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4" />
		</svg>
	);
}
